function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));

    this.querySelector('.mobile-facets__close-btn').addEventListener('click', this.closeMenuDrawer.bind(this));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    console.log('Opener = ', opener);
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class DeferredProductMedia extends HTMLElement {
  constructor() {
    super();
    this.expanded = false; // 是否展开，默认为false不展开
    this.loadMoreButton = this.querySelector('.load-more-button');
    this.pageScrollTop = 0;
    if (this.loadMoreButton) {
      this.loadMoreButton.addEventListener('click', this.loadContent.bind(this));
    }
  }

  loadContent(event) {
    if (!this.expanded) {
      this.pageScrollTop = window.pageYOffset || document.documentElement.scrollTop;

      const template = this.querySelector('#product-more');
      const ul = this.querySelector('ul');

      const content = template.content.cloneNode(true);
      ul.appendChild(content);
      this.expanded = true;
      this.querySelector('.load-more-button').textContent = 'See Less';
    } else {
      window.scrollTo({
        top: this.pageScrollTop,
        behavior: 'smooth',
      });
      const ul = this.querySelector('ul');
      const items = ul.querySelectorAll('li');
      const limit = 6;

      if (items.length > limit) {
        for (let i = limit; i < items.length; i++) {
          ul.removeChild(items[i]);
        }
      }

      this.querySelector('.load-more-button').textContent = 'See More';
      this.expanded = false;
    }
  }
}

customElements.define('deferred-product-media', DeferredProductMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.currentVariant = this.getVariantData(target.id);
      this.updateSelectedSwatchValue(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectedSwatchValue({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  getVariantData(inputId) {
    return JSON.parse(this.getVariantDataElement(inputId).textContent);
  }

  getVariantDataElement(inputId) {
    return this.querySelector(`script[type="application/json"][data-resource="${inputId}"]`);
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select, fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CustomCheckbox extends HTMLElement {
  constructor() {
    super();
    this.status = this.getAttribute('status');
    this.unCheckBtn = this.querySelector('.checkbox-uncheck');
    this.checkBtn = this.querySelector('.checkbox-checked');
    this.disableIcon = this.querySelector('.checkbox-disabled');
    this.addEventListener('click', this.handleClick.bind(this));
    this.showByStatus();
  }

  handleClick() {
    if (this.status === 'checked') {
      this.updateStatus('unchecked');
      this.hideBtn(this.checkBtn);
      this.showByStatus();
    } else {
      this.updateStatus('checked');
      this.hideBtn(this.unCheckBtn);
      this.showByStatus();
    }
  }

  updateStatus(newStatus) {
    this.status = newStatus;
    this.setAttribute('status', this.status);
  }

  showByStatus() {
    if (this.status === 'checked') {
      this.showBtn(this.checkBtn);
    } else if (this.status === 'disabled') {
      this.showBtn(this.disableIcon);
    } else {
      this.showBtn(this.unCheckBtn);
    }
  }

  showBtn(target) {
    target.style.display = 'block';
  }

  hideBtn(target) {
    target.style.display = 'none';
  }
}

customElements.define('custom-checkbox', CustomCheckbox);

const BASE_FORM_INVALID_CLASS_NAME = 'input-field-invalid';

class ProForm extends HTMLElement {
  constructor() {
    super();
    this.rememberMe = false;
    this.showPassword = false;
    this.novalidate = this.getAttribute('novalidate');
    this.closeEyeIcon = this.querySelector('.input-icon__eye-open');
    this.openEyeIcon = this.querySelector('.input-icon__eye-close');
    this.eyeBtn = this.querySelector('.input-icon-wrapper');
    this.passwordInput = this.querySelector('#customerPassword2');
    this.passwordInputs = this.querySelectorAll('input[type=password]');
    this.showPasswordBtns = this.querySelectorAll('input[type=password] + .input-icon-wrapper');

    this.showPasswordBtns.forEach((btn, index) => {
      const inputItem = this.passwordInputs[index];
      this.initPasswordShow(btn, inputItem);
      btn.addEventListener('click', () => {
        if (inputItem.getAttribute('data-password-show') === '1') {
          inputItem.removeAttribute('data-password-show');
        } else {
          inputItem.setAttribute('data-password-show', '1');
        }
        this.initPasswordShow(btn, inputItem);
      });
    });

    this.submitBtn = this.querySelector('.login-submit-btn');
    this.form = this.querySelector('form');
    const email = this.querySelector('#CustomerEmail2');

    const _this = this;

    // 监听form的submit事件，支持回车和点击
    this.form.addEventListener('submit', function (e) {
      e.preventDefault();
      const inputs = _this.querySelectorAll('.input-field input');
      if (!_this.checkRequired(inputs) && _this.checkEmail(email)) {
        if (_this.novalidate) {
          _this.form.submit();
        } else if (_this.checkPassword(_this.passwordInput)) {
          _this.form.submit();
        }
      }
    });
  }

  initPasswordShow(btn, input) {
    const closeEyeIcon = btn.querySelector('.input-icon__eye-open');
    const openEyeIcon = btn.querySelector('.input-icon__eye-close');
    const showPassword = input.getAttribute('data-password-show') === '1';

    if (showPassword) {
      openEyeIcon.style.display = 'block';
      closeEyeIcon.style.display = 'none';
    } else {
      openEyeIcon.style.display = 'none';
      closeEyeIcon.style.display = 'block';
    }
    if (input) {
      input.type = showPassword ? 'text' : 'password';
    }
  }

  showErrorMsg(input, msg) {
    const fieldItem = input.parentElement.parentElement;
    if (!fieldItem.classList.contains(BASE_FORM_INVALID_CLASS_NAME)) {
      fieldItem.classList.add(BASE_FORM_INVALID_CLASS_NAME);
    }

    fieldItem.querySelector('.input-error-message').innerHTML = msg;
  }

  showSuccess(input) {
    const fieldItem = input.parentElement.parentElement;
    if (fieldItem.classList.contains(BASE_FORM_INVALID_CLASS_NAME)) {
      fieldItem.classList.remove(BASE_FORM_INVALID_CLASS_NAME);
    }
    fieldItem.querySelector('.input-error-message').innerHTML = '';
  }

  checkRequired(inputs) {
    let hasEmpty = false;
    const _this = this;
    inputs.forEach(function (input) {
      if (input.value.trim() === '') {
        _this.showErrorMsg(input, 'The field is required');
        hasEmpty = true;
      } else {
        _this.showSuccess(input);
      }
    });

    return hasEmpty;
  }

  checkEmail(input) {
    var emailReg =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (emailReg.test(input.value.trim())) {
      this.showSuccess(input);
      return true;
    }

    this.showErrorMsg(input, 'Please enter a valid email address');
    return false;
  }

  checkPassword(input) {
    const password = input.value;

    // 检查密码长度是否在 8 到 25 个字符之间
    const lengthCheck = password.length >= 8 && password.length <= 25;

    // 检查是否包含至少一个大写字母
    const upperCaseCheck = /[A-Z]/.test(password);

    // 检查是否包含至少一个小写字母
    const lowerCaseCheck = /[a-z]/.test(password);

    // 检查是否包含至少一个数字
    const digitCheck = /\d/.test(password);

    // 如果所有条件都满足，则返回 true，否则返回 false
    // const flag = lengthCheck && upperCaseCheck && lowerCaseCheck && digitCheck;
    const flag = lengthCheck;
    if (!flag) {
      this.showErrorMsg(input, 'Password does not match requirements.');
    } else {
      this.showSuccess(input);
    }
    return flag;
  }
}

customElements.define('pro-form', ProForm);

class RegisterForm extends ProForm {
  constructor() {
    super();
    console.log('register form init!!!');
  }
}

customElements.define('register-form', RegisterForm);

class NTechMedia extends HTMLElement {
  constructor() {
    super();
    this.mediaId = this.getAttribute('data-media-id');
    this.summary = this.querySelector('summary');
    this.details = this.querySelector('details');

    this.summary.addEventListener('click', (event) => this.onMediaClick(event));
    // 关闭折叠
  }

  onMediaClick(event) {
    if (this.details.open) {
      event.preventDefault();
      return;
    }

    this.parentNode.querySelectorAll('n-tech-media').forEach(function (item) {
      if (item.details.open && item !== this) {
        item.details.removeAttribute('open');
      }
    });

    const _this = this;
    document.querySelectorAll('.collapse-tech__image').forEach(function (item) {
      if (item.classList.contains('collapse-tech__image-active')) {
        item.classList.remove('collapse-tech__image-active');
      }

      if (item.getAttribute('data-media-id') === _this.mediaId) {
        item.classList.add('collapse-tech__image-active');
      }
    });
  }
}

customElements.define('n-tech-media', NTechMedia);

class NTechVideo extends HTMLElement {
  constructor() {
    super();
    this.playButton = this.querySelector('.play-button-wrapper');
    this.status = this.getAttribute('data-status'); // 1 是正在播放，0 是停止
    this.video = this.querySelector('video');

    if (this.video.paused) {
      // 没有自动播放
      this.status = '0';
      this.setStatus('0');
    }

    if (this.playButton) {
      this.playButton.addEventListener('click', this.onPlayClick.bind(this));
    }

    this.video.addEventListener('play', () => {
      this.status = '1';
      this.setStatus('1');
    });

    this.video.addEventListener('pause', () => {
      this.status = '0';
      this.setStatus('0');
    });
  }

  setStatus(status) {
    this.setAttribute('data-status', status);
  }

  onPlayClick() {
    // console.log('Status =', this.status);
    if (this.status === '1') {
      // 正在播放视频，触发停止播放视频
      this.video.pause();
      // this.status = '0';
      // this.setStatus('0');
    } else {
      this.video.play();
      // this.status = '1';
      // this.setStatus('1');
    }
  }
}

customElements.define('n-tech-video', NTechVideo);

class VideoPlayer extends HTMLElement {
  constructor() {
    super();
  }
}

customElements.define('video-player', VideoPlayer);

class BlogList extends HTMLElement {
  constructor() {
    super();

    this.loadMoreButton = this.querySelector('.load-more');
    this.dataUrl = this.getAttribute('data-url');
    this.id = this.getAttribute('data-id');
    if (this.loadMoreButton) {
      this.loadMoreButton.addEventListener('click', this.loadContent.bind(this));
    }
  }

  loadContent() {
    if (this.loadMoreButton.hasAttribute('loading')) {
      return;
    }

    const currentPage = parseInt(this.loadMoreButton.getAttribute('data-current-page'));
    const nextPage = currentPage + 1;

    const url = new URL(this.dataUrl, window.location.origin);
    url.searchParams.set('page', nextPage);

    const currentView = new URL(window.location.href).searchParams.get('view');
    if (currentView) {
      url.searchParams.set('view', currentView);
    }

    this.url = url.toString();

    this.loadMoreButton.setAttribute('loading', true);
    const loadingSpinner = this.loadMoreButton.querySelector('.loading__spinner');
    loadingSpinner.classList.remove('hidden');

    fetch(this.url)
      .then((response) => response.text())
      .then((text) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        // load article
        const newArticles = doc.querySelector(`#${this.id} .main-blog__section-list`);
        this.querySelector(`.main-blog__section-list`).innerHTML += newArticles.innerHTML;
        // load button
        const newButton = doc.querySelector(`#${this.id} .load-more`);
        if (!newButton) {
          // 没有额外数据了，移除原有的button
          this.querySelector('.main-blog__load-more').remove();
        } else {
          this.loadMoreButton.setAttribute('data-current-page', newButton.getAttribute('data-current-page'));
        }
      })
      .finally(() => {
        this.loadMoreButton.removeAttribute('loading');
        if (!loadingSpinner.classList.contains('hidden')) {
          loadingSpinner.classList.add('hidden');
        }
      });
  }
}

customElements.define('blog-list', BlogList);

class CustomSelect extends HTMLElement {
  constructor() {
    super();

    const _this = this;
    this.querySelector('.select-selected').addEventListener('click', function (e) {
      e.stopPropagation();
      this.nextElementSibling.classList.toggle('select-hide');
    });

    this.querySelectorAll('.select-items div').forEach(function (item) {
      item.addEventListener('click', function () {
        _this.querySelector('.select-selected').innerHTML = this.innerHTML;
        this.parentElement.classList.add('select-hide');
      });
    });
  }
}

customElements.define('custom-select', CustomSelect);

class CountDownTimer extends HTMLElement {
  constructor() {
    super();
    this.time = this.getAttribute('data-time');
    const countdownDate = new Date(this.time);
    const countdownTimer = setInterval(() => {
      const now = new Date();
      const diff = countdownDate - now;
      if (diff <= 0) {
        clearInterval(countdownTimer);
        return;
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      this.querySelector('.announcement-bar__itmer-day .announcement-bar__itmer-value').textContent = days
        .toString()
        .padStart(2, '0');
      this.querySelector('.announcement-bar__itmer-hour .announcement-bar__itmer-value').textContent = hours
        .toString()
        .padStart(2, '0');
      this.querySelector('.announcement-bar__itmer-minute .announcement-bar__itmer-value').textContent = minutes
        .toString()
        .padStart(2, '0');
      this.querySelector('.announcement-bar__itmer-second .announcement-bar__itmer-value').textContent = seconds
        .toString()
        .padStart(2, '0');
    }, 1000);
  }
}

customElements.define('count-down-timer', CountDownTimer);

class PreviewCart extends HTMLElement {
  constructor() {
    super();
    this.enter = false;
    this.details = this.querySelector('details');
    this.enterTimer = null;
    // onMouseEnter
    this.addEventListener('mouseenter', this.onMouseEnter.bind(this));
    this.addEventListener('click', (event) => {
      // event.preventDefault();
      clearTimeout(this.enterTimer);
    });

    this.addEventListener('mouseleave', this.onMouseLeave.bind(this));

    // onMouseLeave
    this.querySelectorAll('.cart-hover-close-button').forEach((element) => {
      element.addEventListener('click', this.close.bind(this));
    });
  }

  onMouseEnter() {
    if (window.location.pathname === '/cart') {
      return;
    }
    this.enterTimer = setTimeout(() => {
      this.enter = true;
      this.open();
    }, 200);
  }

  onMouseLeave() {
    clearTimeout(this.enterTimer);
    this.enter = false;
    this.close();
  }

  close() {
    this.details.open = false;
  }

  open() {
    this.details.open = true;
  }
}

customElements.define('preview-cart', PreviewCart);

class EstimateDelivery extends HTMLElement {
  constructor() {
    super();
    this.rangeDate = this.dataset.range?.split(',');
    this.startDate = Number(this.rangeDate[0]);
    this.endDate = Number(this.rangeDate[1]);
    this.querySelector('.product__delivery').innerHTML = this.formatDeliveryEstimate(this.startDate, this.endDate);
  }

  formatDeliveryEstimate(daysStart, daysEnd) {
    const currentDate = new Date();

    // Add days to get the start and end dates
    const startDate = new Date(currentDate);
    startDate.setDate(currentDate.getDate() + daysStart);

    const endDate = new Date(currentDate);
    endDate.setDate(currentDate.getDate() + daysEnd);

    // Options for formatting the date to "MMM d"
    const options = { month: 'short', day: 'numeric' };

    // Format the dates to "Sep 7" style
    const formattedStart = startDate.toLocaleDateString('en-US', options);
    const formattedEnd = endDate.toLocaleDateString('en-US', options);

    return `Delivery Estimated Within ${formattedStart} – ${formattedEnd}`;
  }
}

customElements.define('estimate-delivery', EstimateDelivery);

class PromotionBar extends HTMLElement {
  constructor() {
    super();

    console.log('Promotion Bar!');
  }
}

customElements.define('promotion-bar', PromotionBar);

class StickyHeader extends HTMLElement {
  constructor() {
    super();
    this.headerContainer = this.querySelector('.header');
    this.header = this.querySelector('.header-mobile');
    this.headerShow = true;
    const ob = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // 隐藏悬浮header
          this.header.classList.remove('header-mobile-fixed');
          this.querySelector('.header-mobile-placeholder').style.display = 'none';
          this.headerShow = true;
          document.body.style.setProperty('--announcement-bar-height', '40px');
          return;
        }
        // 打开悬浮header
        this.header.classList.add('header-mobile-fixed');
        this.querySelector('.header-mobile-placeholder').style.display = 'block';
        document.body.style.setProperty('--announcement-bar-height', '0px');
        this.headerShow = false;
      });
    });

    ob.observe(this.headerContainer);
  }

  connectedCallback() {
    // let beforeScrollTop = 0;
    // const debounceOnScroll = debounce(() => {
    //   const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    // }, 200);
    // window.addEventListener('scroll', debounceOnScroll);
  }

  disconnectedCallback() {}
}

customElements.define('sticky-header', StickyHeader);

class StickyElement extends HTMLElement {
  constructor() {
    super();
    this.placeholder = document.createElement("div");
    this.placeholder.classList.add("placeholder");
    this.index = this.dataset.index;
    
    // 定义三个导航栏元素配置（与 top-navagation-bar.liquid 保持一致）
    this.navBars = [
      {
        element: null,
        selector: '.announcement-bar-fixed',
        heightVar: '--announcement-bar-height'
      },
      {
        element: null,
        selector: 'promotion-bar',
        heightVar: '--promotion-bar-height'
      },
      {
        element: null,
        selector: '.header-sticky',
        heightVar: '--header-height'
      }
    ];
    
    this.ticking = false;
    this.resizeTicking = false;
    this.mutationObserver = null;
    this.boundHandleScroll = this.handleScroll.bind(this);
    this.boundHandleResize = this.handleResize.bind(this);
  }

  // 检测是否是移动端
  isMobile() {
    return window.innerWidth < 750;
  }

  // 获取元素的实际可见高度（与 top-navagation-bar.liquid 逻辑一致）
  getElementVisibleHeight(element) {
    if (!element) return 0;
    
    // 检查元素的 display 样式
    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') return 0;
    
    // 如果元素有 scroll-hide 类，说明被隐藏了
    if (element.classList.contains('scroll-hide')) return 0;
    
    // 获取元素的实际高度
    const rect = element.getBoundingClientRect();
    const actualHeight = rect.height || 0;
    
    // 如果是移动端且是 promotion-bar，检查是否应该显示
    if (this.isMobile() && element.tagName === 'PROMOTION-BAR') {
      // 检查是否有移动端内容
      const mobileList = element.querySelector('.promotion-bar-list-mobile');
      const pcList = element.querySelector('.promotion-bar-list');
      // 如果只有 PC 列表（没有移动端列表），在移动端高度为 0
      if (pcList && !mobileList) {
        return 0;
      }
    }
    
    return actualHeight;
  }

  // 计算导航栏的总高度
  calculateTopValue() {
    let totalHeight = 0;
    
    // 遍历所有导航栏元素，计算总高度
    this.navBars.forEach(bar => {
      if (bar.element) {
        const visibleHeight = this.getElementVisibleHeight(bar.element);
        if (visibleHeight > 0) {
          totalHeight += visibleHeight;
        }
      }
    });
    
    return totalHeight;
  }

  // 初始化导航栏元素引用
  initNavBars() {
    this.navBars.forEach(bar => {
      bar.element = document.querySelector(bar.selector);
    });
  }

  // 设置 MutationObserver 监听导航栏元素的变化
  setupMutationObserver() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    
    this.mutationObserver = new MutationObserver(() => {
      this.updateTopValue();
    });
    
    this.navBars.forEach(bar => {
      if (bar.element) {
        this.mutationObserver.observe(bar.element, {
          attributes: true,
          attributeFilter: ['class']
        });
      }
    });
  }

  // 更新 top 值
  updateTopValue() {
    if (!this.stickyElement || this.stickyElement.style.position !== 'fixed') {
      return;
    }
    
    const topValue = this.calculateTopValue();
    this.stickyElement.style.top = `${topValue}px`;
  }

  // 处理窗口大小改变
  handleResize() {
    if (!this.resizeTicking) {
      window.requestAnimationFrame(() => {
        // 重新获取元素引用（可能 DOM 结构改变）
        this.initNavBars();
        this.setupMutationObserver();
        this.updateTopValue();
        this.resizeTicking = false;
      });
      this.resizeTicking = true;
    }
  }

  connectedCallback() {
    // 插入占位符到自定义元素的前面
    this.parentNode.insertBefore(this.placeholder, this);

    // 获取 sticky 元素的高度和初始偏移量
    this.stickyElement = this.firstElementChild;
    this.offset = this.stickyElement.offsetTop;
    this.stickyHeight = this.stickyElement.offsetHeight;

    // 初始化导航栏元素引用
    this.initNavBars();
    
    // 设置 MutationObserver
    this.setupMutationObserver();
    
    // 监听滚动事件
    window.addEventListener("scroll", this.boundHandleScroll, { passive: true });
    
    // 监听窗口大小改变
    window.addEventListener("resize", this.boundHandleResize, { passive: true });
    
    // 定期检查元素是否存在（处理动态加载的情况）
    this.checkInterval = setInterval(() => {
      let needsUpdate = false;
      this.navBars.forEach(bar => {
        if (!bar.element) {
          bar.element = document.querySelector(bar.selector);
          if (bar.element) {
            needsUpdate = true;
          }
        }
      });
      if (needsUpdate) {
        this.setupMutationObserver();
        this.updateTopValue();
      }
    }, 1000);
  }

  handleScroll() {
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        
        if (scrollTop > this.offset) {
          // 当滚动超出 offset，显示占位符并固定 sticky 元素
          this.placeholder.style.display = "block";
          this.placeholder.style.height = `${this.stickyHeight}px`;

          this.stickyElement.style.position = "fixed";
          
          // 动态计算 top 值（每次滚动都更新，因为导航栏可能会在滚动时隐藏/显示）
          const topValue = this.calculateTopValue();
          this.stickyElement.style.top = `${topValue}px`;

          this.stickyElement.style.width = "100%";
          this.stickyElement.style.background = "#fff";
          this.stickyElement.style.zIndex = this.index ? this.index : 2;
        } else {
          // 当滚动回到原位置，恢复原来的布局
          this.placeholder.style.display = "none";
          this.stickyElement.style.position = "relative";
          this.stickyElement.style.top = "initial";
          this.stickyElement.style.background = "transparent";
          this.stickyElement.style.zIndex = "auto";
        }
        
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  disconnectedCallback() {
    // 当元素从 DOM 中移除时，移除事件监听
    window.removeEventListener("scroll", this.boundHandleScroll);
    window.removeEventListener("resize", this.boundHandleResize);
    
    // 断开 MutationObserver
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
    
    // 清除定时器
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }
}

customElements.define('sticky-element', StickyElement);

class ModalVariantPickerForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

    this.submitButton = this.querySelector('.submit-button');

    if (this.submitButton) {
      this.submitButton.addEventListener('click', () => {
        console.log('onClick on Submit button');
        this.form.submit();
      });
    }
  }

  onSubmitHandler(event) {
    event.preventDefault();
    console.log('开始执行submit');
  }
}

customElements.define('modal-variant-picker-form', ModalVariantPickerForm);

class CollectionProductGrid extends HTMLElement {
  constructor() {
    super();
    this.template = this.querySelector('template');
    this.loadMoreButton = this.querySelector('button.load-more');
    this.list = this.querySelector('ul');
    this.currentPage = 1;
    this.pageSize = this.dataset.pageSize;

    if (this.loadMoreButton) {
      this.loadMoreButton.addEventListener('click', this.onLoadMoreClick.bind(this));
    }
  }

  onLoadMoreClick() {
    this.currentPage++;
    const list = document.importNode(this.template.content, true);
    this.list.appendChild(list);
    this.loadMoreButton.setAttribute('data-loaded', true);
  }
}

customElements.define('collection-product-grid', CollectionProductGrid);

class CopyItem extends HTMLElement {
  constructor() {
    super();
    this.text = this.getAttribute('data-text');
    this.copyItem = this.querySelector('[data-copy]');
    this.copied = this.getAttribute('data-copied');
    this.addEventListener('click', this.copyToClipboard.bind(this));
  }

  copyToClipboard() {
    if (this.getAttribute('data-copied')) {
      return;
    }
    const text = this.text;
    if (navigator.clipboard && window.isSecureContext) {
      // 如果支持 Clipboard API 且是安全上下文
      navigator.clipboard
        .writeText(text)
        .then(() => {
          const oldContent = this.copyItem.innerHTML;
          this.copyItem.innerHTML = 'Copied';
          this.setAttribute('data-copied', true);
          setTimeout(() => {
            this.copyItem.innerHTML = oldContent;
            this.removeAttribute('data-copied');
          }, 2000);
        })
        .catch((err) => {
          console.error('Failed to copy text: ', err);
        });
    } else {
      // 处理不支持 Clipboard API 的情况
      const textArea = document.createElement('textarea');
      textArea.value = text;
      // 防止页面跳动
      textArea.style.position = 'fixed';
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.width = '2em';
      textArea.style.height = '2em';
      textArea.style.padding = '0';
      textArea.style.border = 'none';
      textArea.style.outline = 'none';
      textArea.style.boxShadow = 'none';
      textArea.style.background = 'transparent';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const successful = document.execCommand('copy');
        console.log(successful ? 'Text copied to clipboard successfully!' : 'Failed to copy text.');
      } catch (err) {
        console.error('Failed to copy text: ', err);
      }

      document.body.removeChild(textArea);
    }
  }
}

customElements.define('copy-item', CopyItem);

// 自定义：锚点跳转按钮（通过 CSS selector 平滑滚动到目标位置）
class AnchorButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  handleKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handleClick();
    }
  }

  handleClick() {
    const selector = this.getAttribute('data-anchor-target');
    if (!selector) return;
    try {
      const target = document.querySelector(selector);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (err) {
      console.warn('anchor-button: invalid selector', selector, err);
    }
  }
}

if (!window.customElements.get('anchor-button')) {
  customElements.define('anchor-button', AnchorButton);
}
